import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import { LuPlus, LuPencil, LuTrash2, LuSearch, LuMapPin, LuExternalLink } from 'react-icons/lu';

const EMPTY = { name: '', lat: '', lng: '', status: 'active' };

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const PAGE_SIZE = 10;
  const [modal, setModal] = useState({ open: false, mode: 'add', data: null });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (search) params.set('search', search);
      const { data } = await api.get(`/locations?${params}`);
      setLocations(data.data || []);
      setPagination(data.pagination || null);
    } catch { toast.error('Failed to load locations'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const openAdd = () => { setForm(EMPTY); setModal({ open: true, mode: 'add', data: null }); };
  const openEdit = r => {
    setForm({ name: r.name, lat: r.lat, lng: r.lng, status: r.status });
    setModal({ open: true, mode: 'edit', data: r });
  };
  const close = () => setModal({ open: false, mode: 'add', data: null });

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal.mode === 'add') await api.post('/locations', form);
      else await api.put(`/locations/${modal.data._id}`, form);
      toast.success(modal.mode === 'add' ? 'Location added!' : 'Updated!');
      close(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    try { await api.delete(`/locations/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const openInMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const prevSearch = useRef(search);
  useEffect(() => { if (prevSearch.current !== search) { prevSearch.current = search; setPage(1); } });

  const columns = [
    { key: 'name', label: 'Location Name', render: r => (
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-hajj-gold/10 flex items-center justify-center flex-shrink-0">
          <LuMapPin size={13} className="text-hajj-gold" />
        </div>
        <span className="font-semibold text-hajj-dark">{r.name}</span>
      </div>
    )},
    { key: 'lat', label: 'Latitude', render: r => <span className="font-mono text-sm text-gray-600">{Number(r.lat).toFixed(6)}</span> },
    { key: 'lng', label: 'Longitude', render: r => <span className="font-mono text-sm text-gray-600">{Number(r.lng).toFixed(6)}</span> },
    { key: 'status', label: 'Status', render: r => (
      <span className={`badge ${r.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>
    )},
    { key: 'actions', label: '', width: 100, render: r => (
      <div className="flex gap-1.5">
        <button onClick={() => openInMaps(r.lat, r.lng)} title="View on Google Maps"
          className="w-8 h-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center hover:bg-blue-100 transition-colors">
          <LuExternalLink size={14} />
        </button>
        <button onClick={() => openEdit(r)}
          className="w-8 h-8 rounded-lg bg-hajj-teal/10 text-teal-700 flex items-center justify-center hover:bg-hajj-teal/20 transition-colors">
          <LuPencil size={14} />
        </button>
        <button onClick={() => handleDelete(r._id)}
          className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors">
          <LuTrash2 size={14} />
        </button>
      </div>
    )},
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 text-hajj-dark">
          <LuMapPin className="text-xl sm:text-2xl" />
          <h1 className="text-xl sm:text-2xl font-extrabold">Locations</h1>
        </div>
        <button className="btn-primary text-sm" onClick={openAdd}><LuPlus size={15} /> Add Location</button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
        <div className="relative w-full sm:max-w-xs">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input placeholder="Search locations..." value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9 w-full" />
        </div>
        <span className="text-sm text-gray-400 sm:ml-auto">{pagination?.total ?? locations.length} locations</span>
      </div>

      <div className="card">
        <Table columns={columns} data={locations} loading={loading} emptyMessage="No locations found" />
        <Pagination page={page} total={pagination?.total || 0} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      <Modal isOpen={modal.open} onClose={close} title={modal.mode === 'add' ? 'Add Location' : 'Edit Location'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Location Name *</label>
              <input value={form.name} onChange={set('name')} required className="input-field" placeholder="e.g. Masjid Al-Haram, Makkah" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Latitude *</label>
              <input
                type="number" step="any"
                value={form.lat} onChange={set('lat')} required
                className="input-field font-mono"
                placeholder="e.g. 21.422510"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Longitude *</label>
              <input
                type="number" step="any"
                value={form.lng} onChange={set('lng')} required
                className="input-field font-mono"
                placeholder="e.g. 39.826168"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
              <select value={form.status} onChange={set('status')} className="input-field">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            {form.lat && form.lng && (
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => openInMaps(form.lat, form.lng)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors w-full justify-center"
                >
                  <LuExternalLink size={14} /> Preview on Google Maps
                </button>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {modal.mode === 'add' ? 'Add Location' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
